import { KeyboardEvent, useMemo, useRef, useState } from 'react';
import { CalendarClock, LayoutGrid, List, MoreVertical, Plus, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cn } from '@Utils';

import { Table } from '@Components';
import Badge from '@Components/Badge/Badge';
import { Button } from '@Components/ui-v2/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@Components/ui-v2/dropdown-menu';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@Components/ui-v2/empty';
import { Input } from '@Components/ui-v2/input';
import SegmentedControl from '@Components/ui-v2/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@Components/ui-v2/select';
import { useHasPermission } from '@CustomHooks';
import { FieldServiceListItem, Permission, Schedule, ScheduleStatus } from '@Models';
import { assignFieldServiceColors } from '../FieldServices/assignFieldServiceColor';
import {
  getRetailerName,
  PAGE_FILTER_BAND_CLASS_NAME,
  PAGE_HEADER_BAND_CLASS_NAME,
  TABLE_HEADER_CELL_CLASS_NAME,
  TABLE_SURFACE_CLASS_NAME,
  UPPERCASE_LABEL_CLASS_NAME,
} from '../FieldServices/constants';
import DeleteScheduleDialog from './components/deleteScheduleDialog';
import FirmAvatarStack from './components/firmAvatarStack';
import ScheduleCard from './components/scheduleCard';
import { formatScheduleRecurrence } from './Wizard/constants';
import getScheduleStatusVariant, { COMPACT_SELECT_TRIGGER_CLASS_NAME } from './constants';
import { getScheduleEnrichment, ScheduleEnrichment } from './enrichment';

type ScheduleView = 'table' | 'cards';
type StatusFilter = ScheduleStatus | 'all';

interface FilterOption {
  value: string;
  label: string;
}

interface SchedulesProps {
  schedules: Schedule[];
  enrichmentById: Record<string, ScheduleEnrichment>;
  fieldServices: FieldServiceListItem[];
  onSchedulesChanged: () => Promise<void>;
}

const STATUS_FILTER_VALUES: StatusFilter[] = ['all', 'active', 'paused', 'ended'];
const ALL_FIELD_SERVICES = 'all';

const Schedules = ({ schedules, enrichmentById, fieldServices, onSchedulesChanged }: SchedulesProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canManage = useHasPermission(Permission.ManageSchedules);
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ScheduleView>('cards');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [firmFilter, setFirmFilter] = useState<string>(ALL_FIELD_SERVICES);
  const [scheduleToDelete, setScheduleToDelete] = useState<Schedule | null>(null);
  const pendingDeleteRef = useRef<Schedule | null>(null);

  const colorsById = useMemo(() => assignFieldServiceColors(fieldServices.map((fs) => fs.id)), [fieldServices]);

  const statusSelectOptions: FilterOption[] = useMemo(
    () => STATUS_FILTER_VALUES.map((value) => ({ value, label: t(`Schedule.StatusFilter.${value}`) })),
    [t],
  );

  const fieldServiceSelectOptions: FilterOption[] = useMemo(() => {
    const byId = new Map<string, string>();
    fieldServices.forEach((fieldService) => byId.set(fieldService.id, fieldService.name));
    return [
      { value: ALL_FIELD_SERVICES, label: t('Schedule.Filters.AllFieldServices') },
      ...Array.from(byId, ([id, name]) => ({ value: id, label: name })),
    ];
  }, [fieldServices, t]);

  const filteredSchedules = useMemo(() => {
    const query = search.trim().toLowerCase();
    return schedules.filter((schedule) => {
      if (statusFilter !== 'all' && schedule.status !== statusFilter) return false;
      if (firmFilter !== ALL_FIELD_SERVICES) {
        const { firmIds } = getScheduleEnrichment(enrichmentById, schedule.id);
        if (!firmIds.includes(firmFilter)) return false;
      }
      if (query && !schedule.name.toLowerCase().includes(query)) return false;
      return true;
    });
  }, [schedules, search, statusFilter, firmFilter, enrichmentById]);

  const columns = useMemo(
    () => [
      {
        Header: t('Schedule.List.FieldServices'),
        accessor: 'firms',
        Cell: ({ row }: { row: { original: Schedule } }) => {
          const { firmIds, firmNames } = getScheduleEnrichment(enrichmentById, row.original.id);
          const firms = firmIds.map((id, index) => ({ id, name: firmNames[index] }));
          return <FirmAvatarStack firms={firms} colorsById={colorsById} />;
        },
      },
      {
        Header: t('Schedule.List.Name'),
        accessor: 'name',
        Cell: ({ row }: { row: { original: Schedule } }) => {
          const { retailers } = getScheduleEnrichment(enrichmentById, row.original.id);
          return (
            <div>
              <div className="font-semibold text-gray-900">{row.original.name}</div>
              {retailers.length > 0 && (
                <div className="mt-0.5 text-xs text-gray-500">{retailers.map(getRetailerName).join(', ')}</div>
              )}
            </div>
          );
        },
      },
      {
        Header: t('Schedule.List.Scope'),
        accessor: 'scope',
        Cell: ({ row }: { row: { original: Schedule } }) => {
          const { storeCount } = getScheduleEnrichment(enrichmentById, row.original.id);
          return <span className="tabular-nums">{t('Schedule.Card.StoresCount', { count: storeCount })}</span>;
        },
      },
      {
        Header: t('Schedule.List.Cadence'),
        accessor: 'cadence',
        Cell: ({ row }: { row: { original: Schedule } }) => (
          <span className="font-semibold text-gray-900">
            {formatScheduleRecurrence(row.original, t, i18n.language)}
          </span>
        ),
      },
      {
        Header: t('Schedule.List.Status'),
        accessor: 'status',
        Cell: ({ row }: { row: { original: Schedule } }) => (
          <Badge
            variant={getScheduleStatusVariant(row.original.status)}
            label={t(`Schedule.Status.${row.original.status}`)}
            showDot
            size="sm"
            className="rounded-full"
          />
        ),
      },
      ...(canManage
        ? [
            {
              accessor: 'id',
              width: 'w-12',
              Cell: ({ row }: { row: { original: Schedule } }) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('Schedule.List.RowActions', { name: row.original.name })}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => event.stopPropagation()}
                      className="inline-flex items-center justify-center rounded p-1 text-gray-400 hover:bg-gray-200/80 hover:text-gray-900 focus:outline-none">
                      <MoreVertical size={16} strokeWidth={1.75} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="min-w-[180px]"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                    onCloseAutoFocus={() => {
                      if (!pendingDeleteRef.current) return;
                      setScheduleToDelete(pendingDeleteRef.current);
                      pendingDeleteRef.current = null;
                    }}>
                    <DropdownMenuItem
                      onSelect={() => {
                        pendingDeleteRef.current = row.original;
                      }}
                      className="cursor-pointer text-red-600">
                      {t('Schedule.Delete.Action')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            },
          ]
        : []),
    ],
    [t, i18n.language, enrichmentById, colorsById, canManage],
  );

  const renderFilterSelect = (
    label: string,
    value: string,
    options: FilterOption[],
    onChange: (next: string) => void,
  ) => (
    <div className="flex items-center gap-2">
      <span className={cn('whitespace-nowrap', UPPERCASE_LABEL_CLASS_NAME)}>{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          aria-label={label}
          className={cn(COMPACT_SELECT_TRIGGER_CLASS_NAME, 'w-auto min-w-[8rem] gap-2')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value} className="text-xs">
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const isFiltered = search.trim() !== '' || statusFilter !== 'all' || firmFilter !== ALL_FIELD_SERVICES;

  return (
    <div className="flex h-full w-full flex-col bg-white">
      <div className={cn('flex items-center justify-between gap-3', PAGE_HEADER_BAND_CLASS_NAME)}>
        <h1 className="truncate text-xl font-bold leading-8 text-gray-900">{t('Schedule.Title')}</h1>
        <Button
          size="sm"
          disabled={!canManage}
          title={canManage ? undefined : t('Schedule.CreateNoPermission')}
          onClick={canManage ? () => navigate('/schedules/new') : undefined}
          className="h-9 flex-shrink-0 text-xs font-semibold">
          <Plus size={16} />
          {t('Schedule.CreateButton')}
        </Button>
      </div>

      <div className={cn('flex flex-wrap items-center justify-between gap-3', PAGE_FILTER_BAND_CLASS_NAME)}>
        <div className="relative w-full max-w-xs">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('Schedule.SearchPlaceholder')}
            aria-label={t('Schedule.SearchPlaceholder')}
            className="h-9 rounded-lg border-gray-300 pl-9 text-xs md:text-xs"
          />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {renderFilterSelect(t('Schedule.Filters.Status'), statusFilter, statusSelectOptions, (next) =>
            setStatusFilter(next as StatusFilter),
          )}
          {renderFilterSelect(t('Schedule.Filters.FieldService'), firmFilter, fieldServiceSelectOptions, setFirmFilter)}
          <SegmentedControl
            ariaLabel={t('Schedule.ViewToggle.AriaLabel')}
            className="flex-shrink-0 gap-0.5 p-0.5"
            optionClassName="flex h-8 w-8 items-center justify-center rounded-md"
            activeOptionClassName="bg-white text-brand-700 shadow-sm"
            inactiveOptionClassName="text-gray-500 hover:text-gray-700"
            value={view}
            onChange={setView}
            options={[
              { value: 'table', label: <List size={15} />, ariaLabel: t('Schedule.ViewToggle.Table') },
              { value: 'cards', label: <LayoutGrid size={15} />, ariaLabel: t('Schedule.ViewToggle.Cards') },
            ]}
          />
        </div>
      </div>

      <div
        className={cn(
          'w-full flex-1 px-4 py-6 sm:px-7',
          view === 'cards' && filteredSchedules.length > 0 && 'bg-background',
        )}>
        {filteredSchedules.length === 0 ? (
          <div className={TABLE_SURFACE_CLASS_NAME}>
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  {isFiltered ? <Search strokeWidth={1.75} /> : <CalendarClock strokeWidth={1.75} />}
                </EmptyMedia>
                <EmptyTitle className="text-base font-semibold text-gray-900">
                  {isFiltered ? t('Schedule.Empty.NoResultsTitle') : t('Schedule.Empty.Title')}
                </EmptyTitle>
                <EmptyDescription>
                  {isFiltered ? t('Schedule.Empty.NoResultsDescription') : t('Schedule.Empty.Description')}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : view === 'table' ? (
          <div className={TABLE_SURFACE_CLASS_NAME}>
            <Table
              columns={columns}
              data={filteredSchedules}
              pageSize={10}
              pagination={filteredSchedules.length > 10}
              headerCellClassName={TABLE_HEADER_CELL_CLASS_NAME}
              getRowClassName={() => 'cursor-pointer'}
              getRowProps={(row: { original: Schedule }) => {
                const openDetail = () => navigate(`/schedules/${row.original.id}`);
                return {
                  tabIndex: 0,
                  onClick: openDetail,
                  onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openDetail();
                    }
                  },
                };
              }}
            />
          </div>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3.5">
            {filteredSchedules.map((schedule) => (
              <ScheduleCard
                key={schedule.id}
                schedule={schedule}
                enrichment={getScheduleEnrichment(enrichmentById, schedule.id)}
                colorsById={colorsById}
              />
            ))}
          </div>
        )}
      </div>
      <DeleteScheduleDialog
        schedule={scheduleToDelete}
        onClose={() => setScheduleToDelete(null)}
        onDeleted={onSchedulesChanged}
      />
    </div>
  );
};

export default Schedules;
